import { useMemo, useState } from "react";
import styles from "../styles/runner.module.css";
import type { OrderSequenceConfig } from "../types";

interface Props {
  prompt: string;
  config: OrderSequenceConfig;
  passingScore: number;
  onScored: (result: {
    score: number;
    passed: boolean;
    raw: { order: string[] };
  }) => void;
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function OrderSequence({ prompt, config, passingScore, onScored }: Props) {
  const initial = useMemo(() => shuffle(config.steps.map((s) => s.id)), [config.steps]);
  const [order, setOrder] = useState<string[]>(initial);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [passed, setPassed] = useState(false);

  const labelById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const s of config.steps) m[s.id] = s.label;
    return m;
  }, [config.steps]);

  function move(idx: number, delta: -1 | 1) {
    if (submitted) return;
    const j = idx + delta;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  }

  function submit() {
    if (submitted) return;
    // Score = 1 - (inversions / max_inversions). Simple Kendall tau-ish.
    const target = config.correct_order;
    const n = target.length;
    if (n !== order.length) return;
    let inversions = 0;
    const maxInv = (n * (n - 1)) / 2;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const ai = target.indexOf(order[i]);
        const aj = target.indexOf(order[j]);
        if (ai > aj) inversions += 1;
      }
    }
    const s = maxInv === 0 ? 1 : 1 - inversions / maxInv;
    const ok = s >= passingScore;
    setScore(s);
    setPassed(ok);
    setSubmitted(true);
    onScored({
      score: s,
      passed: ok,
      raw: { order },
    });
  }

  return (
    <div className={styles.col}>
      <h2 className={styles.prompt}>{prompt}</h2>
      <ol style={{ padding: 0, margin: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
        {order.map((id, idx) => {
          const isCorrect = submitted && config.correct_order[idx] === id;
          const isWrong = submitted && config.correct_order[idx] !== id;
          const cls = [
            styles.dragItem,
            isCorrect ? styles.optionCorrect : "",
            isWrong ? styles.optionWrong : "",
          ].join(" ");
          return (
            <li key={id} className={cls} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 700, color: "#6b7280", minWidth: 24 }}>{idx + 1}.</span>
              <span style={{ flex: 1 }}>{labelById[id]}</span>
              {!submitted && (
                <span style={{ display: "flex", gap: 4 }}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    aria-label="Subir"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => move(idx, +1)}
                    disabled={idx === order.length - 1}
                    aria-label="Bajar"
                  >
                    ↓
                  </button>
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {!submitted ? (
        <button type="button" className={styles.primaryButton} onClick={submit}>
          Validar orden
        </button>
      ) : (
        <div className={passed ? styles.summary : `${styles.summary} ${styles.summaryFail}`}>
          <span className={styles.scoreBadge}>{Math.round(score * 100)}%</span>
          <strong>{passed ? "¡Secuencia correcta!" : "Hay pasos fuera de lugar"}</strong>
          <span>Mínimo requerido: {Math.round(passingScore * 100)}%</span>
        </div>
      )}
    </div>
  );
}
